import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Save, CircleCheck as CheckCircle2, Trash2, ChevronsUpDown } from 'lucide-react';

interface Script {
  id: string;
  version: number;
  title: string;
  is_active: boolean;
  greeting: string | null;
  questions: string[];
  red_flags: string[];
  objection_handlers: Array<{ objection: string; response: string }>;
  value_ranges: { min?: number; max?: number; sweet_spot?: number };
  closing_rules: string | null;
  raw_notes: string | null;
  activated_at: string | null;
  created_at: string;
}

export default function ScriptTab({ sellerId }: { sellerId: string }) {
  const { user } = useAuth();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Script | null>(null);

  useEffect(() => {
    void load();
  }, [sellerId]);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('ai_agent_scripts')
      .select('*')
      .eq('seller_id', sellerId)
      .order('version', { ascending: false });
    if (error) {
      console.error(error);
      setLoading(false);
      return;
    }
    const list = (data ?? []).map(normalize);
    setScripts(list);
    if (list.length > 0) {
      const active = list.find((s) => s.is_active) ?? list[0];
      setSelectedId(active.id);
      setDraft({ ...active });
    } else {
      setSelectedId('');
      setDraft(null);
    }
    setLoading(false);
  }

  function normalize(row: any): Script {
    return {
      id: row.id,
      version: row.version,
      title: row.title,
      is_active: row.is_active,
      greeting: row.greeting,
      questions: Array.isArray(row.questions) ? row.questions : [],
      red_flags: Array.isArray(row.red_flags) ? row.red_flags : [],
      objection_handlers: Array.isArray(row.objection_handlers) ? row.objection_handlers : [],
      value_ranges: row.value_ranges ?? {},
      closing_rules: row.closing_rules,
      raw_notes: row.raw_notes,
      activated_at: row.activated_at,
      created_at: row.created_at,
    };
  }

  async function createVersion() {
    if (!user) return;
    const { data: seller } = await supabase.from('users').select('unit_id').eq('id', sellerId).maybeSingle();
    const nextVersion = scripts.length > 0 ? Math.max(...scripts.map((s) => s.version)) + 1 : 1;
    const base = scripts[0];
    const insertPayload: any = {
      seller_id: sellerId,
      unit_id: (seller as any)?.unit_id ?? null,
      version: nextVersion,
      title: 'Script v' + nextVersion,
      is_active: false,
      greeting: base?.greeting ?? 'Oi! Vi seu interesse em uma proposta. Posso te fazer algumas perguntas rapidas?',
      questions: base?.questions ?? [
        'Voce ja pegou emprestimo com a gente antes?',
        'Qual o valor que voce precisa?',
        'Como voce recebe (salario, aposentadoria, autonomo)?',
      ],
      red_flags: base?.red_flags ?? [],
      objection_handlers: base?.objection_handlers ?? [],
      value_ranges: base?.value_ranges ?? {},
      closing_rules: base?.closing_rules ?? null,
      raw_notes: base?.raw_notes ?? null,
      created_by: user.id,
    };
    const { data, error } = await supabase.from('ai_agent_scripts').insert(insertPayload).select().single();
    if (error) return alert('Erro: ' + error.message);
    await load();
    setSelectedId((data as any).id);
    setDraft(normalize(data));
  }

  async function activate() {
    if (!draft) return;
    const { error: e1 } = await supabase
      .from('ai_agent_scripts')
      .update({ is_active: false })
      .eq('seller_id', sellerId);
    if (e1) return alert('Erro: ' + e1.message);
    const { error } = await supabase
      .from('ai_agent_scripts')
      .update({ is_active: true, activated_at: new Date().toISOString() })
      .eq('id', draft.id);
    if (error) return alert('Erro: ' + error.message);
    await load();
  }

  async function saveDraft() {
    if (!draft) return;
    const { error } = await supabase
      .from('ai_agent_scripts')
      .update({
        title: draft.title,
        greeting: draft.greeting,
        questions: draft.questions,
        red_flags: draft.red_flags,
        objection_handlers: draft.objection_handlers,
        value_ranges: draft.value_ranges,
        closing_rules: draft.closing_rules,
        raw_notes: draft.raw_notes,
      })
      .eq('id', draft.id);
    if (error) return alert('Erro: ' + error.message);
    await load();
  }

  async function removeVersion() {
    if (!draft) return;
    if (draft.is_active) return alert('Nao da para apagar a versao ativa.');
    if (!confirm('Apagar esta versao do script?')) return;
    const { error } = await supabase.from('ai_agent_scripts').delete().eq('id', draft.id);
    if (error) return alert('Erro: ' + error.message);
    await load();
  }

  function selectVersion(id: string) {
    setSelectedId(id);
    const found = scripts.find((s) => s.id === id);
    if (found) setDraft({ ...found });
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  if (!draft) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <p className="mb-4 text-sm text-slate-600">Nenhum script cadastrado para este vendedor ainda.</p>
        <button
          onClick={createVersion}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> Criar primeiro script
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <ChevronsUpDown className="h-4 w-4 text-slate-400" />
          <select
            value={selectedId}
            onChange={(e) => selectVersion(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            {scripts.map((s) => (
              <option key={s.id} value={s.id}>
                v{s.version} — {s.title} {s.is_active ? '(ativa)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={createVersion}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <Plus className="h-4 w-4" /> Nova versao
          </button>
          {!draft.is_active && (
            <button
              onClick={activate}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-4 w-4" /> Ativar esta versao
            </button>
          )}
          <button
            onClick={saveDraft}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Save className="h-4 w-4" /> Salvar
          </button>
          {!draft.is_active && (
            <button
              onClick={removeVersion}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4" /> Apagar
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Field label="Titulo">
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Saudacao (primeira mensagem)">
            <textarea
              value={draft.greeting ?? ''}
              onChange={(e) => setDraft({ ...draft, greeting: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
          <ListEditor
            label="Perguntas (na ordem)"
            items={draft.questions}
            placeholder="Digite uma pergunta e Enter"
            onChange={(items) => setDraft({ ...draft, questions: items })}
          />
          <ListEditor
            label="Sinais de alerta (red flags)"
            items={draft.red_flags}
            placeholder='ex: "so quer saber juros", "some sem responder"'
            onChange={(items) => setDraft({ ...draft, red_flags: items })}
          />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <NumberField
              label="Min (R$)"
              value={draft.value_ranges.min}
              onChange={(v) => setDraft({ ...draft, value_ranges: { ...draft.value_ranges, min: v } })}
            />
            <NumberField
              label="Ideal (R$)"
              value={draft.value_ranges.sweet_spot}
              onChange={(v) => setDraft({ ...draft, value_ranges: { ...draft.value_ranges, sweet_spot: v } })}
            />
            <NumberField
              label="Max (R$)"
              value={draft.value_ranges.max}
              onChange={(v) => setDraft({ ...draft, value_ranges: { ...draft.value_ranges, max: v } })}
            />
          </div>
          <ObjectionsEditor
            items={draft.objection_handlers}
            onChange={(items) => setDraft({ ...draft, objection_handlers: items })}
          />
          <Field label="Regras de fechamento">
            <textarea
              value={draft.closing_rules ?? ''}
              onChange={(e) => setDraft({ ...draft, closing_rules: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Notas internas (nao vao para o lead)">
            <textarea
              value={draft.raw_notes ?? ''}
              onChange={(e) => setDraft({ ...draft, raw_notes: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <Field label={label}>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </Field>
  );
}

function ListEditor({
  label,
  items,
  placeholder,
  onChange,
}: {
  label: string;
  items: string[];
  placeholder: string;
  onChange: (items: string[]) => void;
}) {
  const [input, setInput] = useState('');
  return (
    <Field label={label}>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <span className="mt-0.5 text-xs font-semibold text-slate-400">{idx + 1}.</span>
            <input
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="flex-1 bg-transparent focus:outline-none"
            />
            <button
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-slate-400 hover:text-red-500"
              aria-label="Remover"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.trim()) {
                e.preventDefault();
                onChange([...items, input.trim()]);
                setInput('');
              }
            }}
            placeholder={placeholder}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button
            onClick={() => {
              if (input.trim()) {
                onChange([...items, input.trim()]);
                setInput('');
              }
            }}
            className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Field>
  );
}

function ObjectionsEditor({
  items,
  onChange,
}: {
  items: Array<{ objection: string; response: string }>;
  onChange: (items: Array<{ objection: string; response: string }>) => void;
}) {
  return (
    <Field label="Objecoes e respostas">
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
            <input
              value={item.objection}
              onChange={(e) => {
                const next = [...items];
                next[idx] = { ...item, objection: e.target.value };
                onChange(next);
              }}
              placeholder="Objecao (ex: os juros sao altos)"
              className="mb-1 w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium"
            />
            <textarea
              value={item.response}
              onChange={(e) => {
                const next = [...items];
                next[idx] = { ...item, response: e.target.value };
                onChange(next);
              }}
              placeholder="Como responder"
              rows={2}
              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs"
            />
            <div className="mt-1 flex justify-end">
              <button
                onClick={() => onChange(items.filter((_, i) => i !== idx))}
                className="text-xs text-slate-400 hover:text-red-500"
              >
                Remover
              </button>
            </div>
          </div>
        ))}
        <button
          onClick={() => onChange([...items, { objection: '', response: '' }])}
          className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" /> Adicionar objecao
        </button>
      </div>
    </Field>
  );
}
